/**
 * Methodology Translations Module (English)
 * Research Approach & Data Sources for JCI Development Councils Youth Futures Observatory
 * @module methodology
 * @language en
 */

export const methodology = {
  methodology: {
    // Page Meta
    pageTitle: "Research Approach & Data Sources",
    heroSubtitle: "Evidence-based intelligence empowering JCI National and Local Organizations",
    heroDescription: "The JCI Development Councils Youth Futures Observatory is built on a rigorous, auditable methodology that combines verified data from the JCI, global youth research initiatives, and established economic indices.",
    
    // Hero CTAs
    ctaExplore: "Explore the Methodology",
    ctaContact: "Contact the Team",
    
    // Executive Overview
    executiveOverviewTitle: "Executive Overview",
    executiveOverviewText: "This document outlines the research approach, data sources, and analytical methods used to create the observatory's insights. Every statistic displayed traces directly to verified, auditable sources.",
    coreCommitment: "Every statistic displayed traces directly to verified, auditable sources.",
    
    // Overview Section
    overviewTitle: "The DASHCity Framework",
    overviewDescription: "A comprehensive 5-phase approach designed to transform organizations into high-impact powerhouses through systematic assessment, measurement, and continuous improvement.",
    overviewIntro: "DASHCity provides a structured pathway for organizations to identify opportunities, measure performance, communicate value, establish governance, and scale their impact globally.",
    frameworkFeatures: "DASHCity 5-Phase Framework",
    inclusiveFeature: "Inclusive - Designed for organizations of all sizes and regions",
    scalableFeature: "Scalable - From local to global implementation",
    
    // Framework Characteristics
    characteristicsTitle: "Framework Principles",
    characteristics: {
      evidenceBased: "Evidence-Based",
      evidenceBasedDesc: "Grounded in verified data and research",
      iterative: "Iterative",
      iterativeDesc: "Continuous improvement through feedback loops",
      inclusive: "Inclusive",
      inclusiveDesc: "Designed for councils of all sizes and regions",
      scalable: "Scalable",
      scalableDesc: "From local organizations to global implementation",
      transparent: "Transparent",
      transparentDesc: "Open methodology and data standards",
      actionable: "Actionable",
      actionableDesc: "Practical tools and clear deliverables"
    },
    
    // Phase Status Badges
    statusDone: "DONE",
    statusDoing: "IN PROGRESS",
    statusToDo: "PLANNED",
    
    // Phase Detail Labels
    activitiesLabel: "Activities",
    deliverablesLabel: "Deliverables",
    resultLabel: "Result",
    
    // Section Titles
    dcPartnershipTitle: "Development Council Partnership",
    dataCollectionTitle: "Data Collection Methodology",
    
    // Framework Introduction
    frameworkIntroText: "The foundation phase focuses on understanding the current state of JCI, mapping existing resources, and identifying key areas of opportunity for growth and impact.",
    
    // Phase 1: IDENTIFY
    phase1Title: "IDENTIFY",
    phase1Period: "September - November 2025",
    phase1Objective: "Map the complete youth leadership development ecosystem",
    phase1Description: "The foundation phase focuses on understanding the current state of JCI, mapping existing resources, and identifying key areas of opportunity for growth and impact.",
    phase1Activities: "Strategic mapping, institutional diagnosis, gap analysis, research scope definition",
    phase1Deliverables: "Strategic analysis document, Institutional diagnosis, Data source inventory, Research scope document",
    phase1KeyMetrics: "Council coverage rate, stakeholder engagement level, priority areas identified",
    phase1Result: "Clear strategic justification for research initiative",
    phase1Status: "done",
    phase1WhatWasDone: [
      "Analyzed JCI Strategic Plan 2023-2027",
      "Leveraged 2025 JCI Impact Report data",
      "Identified gap: no centralized analysis platform",
      "Defined research scope: JCI + YFI + Global surveys"
    ],
    
    // Phase 2: MEASURE
    phase2Title: "MEASURE",
    phase2Period: "October - December 2025",
    phase2Objective: "Establish rigorous data standards and verification processes",
    phase2Description: "The measurement phase implements rigorous data collection across multiple sources, establishing baseline metrics and performance indicators.",
    phase2Activities: "Data quality standards, source verification, baseline establishment, validation process implementation",
    phase2Deliverables: "Data quality standards, Source verification matrix, YFI methodology documentation, 40+ auditable data points",
    phase2KeyMetrics: "Data completeness rate, indicator coverage, validation accuracy",
    phase2Result: "40+ auditable data points established",
    phase2Status: "done",
    phase2WhatWasDone: [
      "Established data quality standards (7 criteria)",
      "Verified primary and secondary data sources",
      "Established JCI global metrics baseline",
      "Implemented 5-step validation process"
    ],
    
    // Phase 3: PROMOTE
    phase3Title: "PROMOTE",
    phase3Period: "December 2025 - January 2026",
    phase3Objective: "Communicate value and engage stakeholders",
    phase3Description: "The promotion phase transforms measurement insights into compelling narratives that communicate the value and impact of the observatory.",
    phase3Activities: "Strategic value narrative, communication materials, engagement channels, stakeholder outreach",
    phase3Deliverables: "Multilingual materials, Stakeholder value propositions, Engagement channels, Contribution guidelines",
    phase3KeyMetrics: "Stakeholder reach, engagement rates, message resonance scores",
    phase3Result: "Multilingual communication materials operational",
    phase3Status: "doing",
    phase3WhatWasDone: [
      "Created stakeholder-specific value narratives",
      "Developed materials in 5 languages (EN, PT, ES, FR, JA)",
      "Opened digital engagement channels",
      "Published contribution guidelines"
    ],
    
    // Phase 4: DIRECT
    phase4Title: "DIRECT",
    phase4Period: "January - February 2026",
    phase4Objective: "Establish governance and quality oversight",
    phase4Description: "The direction phase establishes robust governance frameworks, defines roles and responsibilities, and creates sustainable operational structures.",
    phase4Activities: "Governance structure, validation process, update cycles, responsibility definitions",
    phase4Deliverables: "Governance structure, Responsibility definitions, Quality gate checklist, Update cycle schedule",
    phase4KeyMetrics: "Governance adoption rate, decision efficiency, compliance levels",
    phase4Result: "Governance structure with 4 Development Councils",
    phase4Status: "doing",
    phase4WhatWasDone: [
      "Established 4-level governance structure",
      "Defined 7-criteria quality gate checklist",
      "Set annual, intermediate, and continuous update cycles"
    ],
    
    // Phase 5: SCALE
    phase5Title: "SCALE",
    phase5Period: "January 2026 - December 2027+",
    phase5Objective: "Expand reach, impact, and global influence",
    phase5Description: "The scaling phase focuses on expanding the methodology's reach, amplifying impact through network effects, and establishing the framework as a global reference.",
    phase5Activities: "Expansion to 15+ NOs, public APIs, academic partnerships, policy influence",
    phase5Deliverables: "Global Rollout Plan, Public APIs, Academic partnerships, Policy influence documentation",
    phase5KeyMetrics: "Geographic coverage, registered users, media citations",
    phase5Result: "Platform scaling to global authority status",
    phase5Status: "todo",
    phase5Targets2026: [
      "Expand to 15+ National Organizations contributing",
      "Scale to 200+ registered users",
      "Launch public APIs for researchers",
      "10+ annual media articles"
    ],
    phase5Targets2027: [
      "Position as global reference for youth leadership",
      "Integration with UN, WEF, UNESCO platforms",
      "Policy influence in 10+ countries",
      "1,000+ user community"
    ],
    
    // Data Sources Section
    dataSourcesTitle: "Data Sources",
    dataSourcesDescription: "Comprehensive data from verified JCI and global research sources",
    primaryDataSourcesTitle: "Primary Data Sources",
    jciInstitutionalData: "JCI Institutional Data",
    yfiGlobalData: "Youth Futures Index & Global Data",
    secondarySourcesTitle: "Secondary Data Sources",
    
    // Table Column Headers
    sourceColumn: "Source",
    coverageColumn: "Coverage",
    categoryColumn: "Category",
    dimensionColumn: "Dimension",
    weightColumn: "Weight",
    sourcesColumn: "Sources",
    criterionColumn: "Criterion",
    exampleColumn: "Example",
    gapColumn: "Gap",
    mitigationColumn: "Mitigation",
    
    // Primary Data Source Items
    sourceJCIImpactReport: "JCI Impact Report 2025",
    sourceJCIVision2040: "JCI Vision 2040 Strategic Plan",
    sourceJCIMembershipSurvey: "JCI Membership Survey 2025",
    coverageImpactReport: "148,122 members, 4,641 LOs",
    coverageVision2040: "Strategic targets through 2040",
    coverageSurvey: "3,790 respondents",
    categoryJciInstitutional: "JCI Institutional",

    // YFI Dimensions Table
    yfiDimensionsTitle: "Youth Futures Index Dimensions",
    dimensionMentalHealth: "Mental Health",
    dimensionWorkforce: "Workforce & Education",
    dimensionEntrepreneurship: "Entrepreneurship",
    dimensionEducation: "Education",
    coverageCountries: "114 countries",
    sourceMentalHealth: "WHO, Sapien Labs MHQ",
    sourceWorkforce: "ILO, UNESCO, World Bank",
    sourceEntrepreneurship: "World Bank, GEM, ITU",
    
    // Formula Legend
    formulaLabel: "Youth Futures Index Formula",
    formulaLegendMH: "MH = Mental Health",
    formulaLegendWF: "WF = Workforce & Education",
    formulaLegendENT: "ENT = Entrepreneurship",
    
    // DC Partnership Description
    dcPartnershipDescription: "How Development Councils collaborate with the observatory",
    dcProvidedTitle: "What DCs Provided",
    dcNotProvidedTitle: "What DCs Did NOT Provide",
    dcProvidedStrategic: "Strategic collaboration, regional feedback",
    dcProvidedRegional: "Dissemination support across regions",
    dcProvidedValidation: "Regional context on findings, relevance confirmation",
    dcProvidedPlansOfAction: "2025 Plans of Action, regional strategies",
    dcNotProvidedPrimaryData: "Primary research data",
    dcNotProvidedSurveyDesign: "Survey design or execution",
    dcNotProvidedBaseline: "Baseline data collection",
    dcNotProvidedAnalysis: "Data analysis",
    
    // Data Collection Section
    dataCollectionDescription: "Five key areas of data collection and analysis",
    dataCollectionJCI: "JCI Data Consolidation",
    dataCollectionJCIDesc: "Gathered official JCI Impact Report 2025, extracted membership data, organized by region",
    dataCollectionSurvey: "Survey Data Analysis",
    dataCollectionSurveyDesc: "Analyzed 3,790 respondents for demographics, motivations, skills gaps",
    dataCollectionVision: "Vision 2040 Documentation",
    dataCollectionVisionDesc: "Extracted growth targets, strategic pillars, developed scenario planning",
    dataCollectionYFI: "YFI Integration",
    dataCollectionYFIDesc: "Mapped 114 JCI countries to YFI rankings, aggregated regional scores",
    dataCollectionMentalHealth: "Mental Health Deep Dive",
    dataCollectionMentalHealthDesc: "Synthesized WHO data, analyzed Sapien Labs research, connected to JCI RISE",
    
    // Verification Checklist Items
    verifySourceIdentifiable: "Source identifiable",
    verifyDateConfirmed: "Date confirmed",
    verifyMethodologyDocumented: "Methodology documented",
    verifySampleSizeStated: "Sample size stated",
    verifyLimitationsAcknowledged: "Limitations acknowledged",
    verifyCalculationsReproducible: "Calculations reproducible",
    
    // Verification Checklist Examples
    verifyExampleSource: "JCI Impact Report 2025, p.12",
    verifyExampleDate: "Data as of December 2024",
    verifyExampleMethodology: "Survey methodology in appendix",
    verifyExampleSampleSize: "N = 3,790 respondents",
    verifyExampleLimitations: "English-language bias noted",
    verifyExampleCalculations: "YFI formula published",
    
    // Zero Synthetic Data Section
    zeroSyntheticDescription: "The observatory contains zero synthetic data",
    zeroSyntheticItem1: "No projections presented as current facts",
    zeroSyntheticItem2: "No model-generated statistics",
    zeroSyntheticItem3: "No interpolated values",
    zeroSyntheticItem4: "No estimated figures (unless explicitly marked)",
    zeroSyntheticItem5: "No assumed distributions",
    
    // Acknowledged Limitations
    limitationSurveyBias: "Survey response bias",
    mitigationSurveyBias: "Regional weighting applied, bias documented",
    limitationCoverage: "YFI country coverage gaps",
    mitigationCoverage: "Regional averages calculated; gaps noted",
    limitationLagging: "Lagging indicators",
    mitigationLagging: "Most recent data used (2024-2025)",
    limitationLanguage: "Language limitations",
    mitigationLanguage: "Translation in 5 languages; English bias noted",
    
    // Vision 2040 Alignment
    vision2040AlignmentDescription: "How the observatory supports JCI's strategic vision",
    strategyWhereExpand: "Where to Expand",
    strategyWhereExpandDesc: "Priority expansion in Africa & Middle East regions with YFI scores below 55",
    strategyWhatPrioritize: "What to Prioritize",
    strategyWhatPrioritizeDesc: "Mental Health dimension (40% weight) as primary focus area",
    strategyHowScale: "How to Scale",
    strategyHowScaleDesc: "Technology adoption target: 80%+ digital engagement",
    strategySuccessMeasures: "Success Measures",
    strategySuccessMeasuresDesc: "Target: Improve regional YFI scores by 4-7 points by 2030",
    
    // Transparency Section
    transparencyDescription: "Complete methodology transparency and reproducibility",
    reproducibilityTitle: "Reproducibility",
    reproducibilityDescription: "Any researcher can reproduce this analysis using the published sources and methodology",
    noProprietaryTitle: "No Proprietary Methods",
    noProprietaryDescription: "No black-box calculations; all formulas and weights are publicly documented",
    
    // Summary Section
    summaryDescription: "Current status of research methodology implementation",
    summaryCompletedItem1: "Phase 1: Identify",
    summaryCompletedItem2: "Phase 2: Measure",
    summaryCompletedItem3: "Data architecture",
    summaryCompletedItem4: "YFI calculation",
    summaryCompletedItem5: "114 country coverage",
    summaryCompletedItem6: "Multilingual platform",
    summaryCompletedItem7: "DC partnership model",
    summaryInProgressItem1: "Phase 3: Promote",
    summaryInProgressItem2: "Phase 4: Direct",
    summaryInProgressItem3: "User feedback integration",
    summaryInProgressItem4: "Regional deep dives",
    summaryPlannedItem1: "Phase 5: Scale",
    summaryPlannedItem2: "UN/WEF integration",
    summaryPlannedItem3: "API development",

    // Data Integrity Section
    dataIntegrityTitle: "Data Integrity Standards",
    verificationChecklistTitle: "Verification Checklist",
    
    // Zero Synthetic Data
    zeroSyntheticTitle: "Zero Synthetic Data Commitment",
    zeroSyntheticException: "Exception: Scenario projections for Vision 2040 are clearly labeled as 'scenarios' and illustrative, not predictions.",
    
    // Acknowledged Limitations
    acknowledgedLimitationsTitle: "Acknowledged Limitations",
    
    // Measurement Modules (keeping for backward compatibility)
    modulesTitle: "Measurement Modules",
    modulesDescription: "Four thematic areas ensure comprehensive assessment of Development Council performance and impact.",
    
    module1Title: "Strategic Alignment",
    module1Description: "Measures alignment with JCI mission, vision, and global strategic priorities",
    module1Indicators: "Vision clarity, mission adoption, strategic plan quality, global alignment score",
    
    module2Title: "Operational Excellence",
    module2Description: "Assesses operational efficiency, resource management, and organizational health",
    module2Indicators: "Resource utilization, process efficiency, member satisfaction, financial sustainability",
    
    module3Title: "Educational Quality",
    module3Description: "Evaluates training programs, skill development, and knowledge transfer effectiveness",
    module3Indicators: "Program quality scores, learning outcomes, certification rates, trainer effectiveness",
    
    module4Title: "Impact & Transformation",
    module4Description: "Measures tangible outcomes, community impact, and transformational change",
    module4Indicators: "Project completion rates, beneficiary reach, SDG alignment, sustainability metrics",
    
    // Areas of Opportunity (keeping for backward compatibility)
    areasTitle: "Areas of Opportunity",
    areasDescription: "The methodology addresses four interconnected areas where Development Councils can maximize their impact.",
    
    areaIndividual: "Individual Development",
    areaIndividualFocus: "Personal growth and leadership development of members",
    areaIndividualMetrics: "Skills gained, leadership positions held, career advancement",
    
    areaCommunity: "Community Impact",
    areaCommunityFocus: "Positive change in local communities through projects and initiatives",
    areaCommunityMetrics: "Projects completed, beneficiaries reached, sustainable outcomes",
    
    areaInternational: "International Connection",
    areaInternationalFocus: "Global engagement and cross-cultural collaboration",
    areaInternationalMetrics: "International events, partnerships formed, exchange participation",
    
    areaEntrepreneurial: "Entrepreneurial Spirit",
    areaEntrepreneurialFocus: "Innovation, business development, and economic opportunity creation",
    areaEntrepreneurialMetrics: "Businesses supported, innovation programs, economic impact",
    
    // Data Standards
    dataStandardsTitle: "Data Standards",
    dataStandardsDescription: "Rigorous standards ensure data quality, reliability, and comparability across all sources.",
    
    acceptedTitle: "Accepted Data Criteria",
    acceptedVerifiable: "Verifiable through official sources",
    acceptedAuditable: "Auditable with clear documentation trail",
    acceptedTraceable: "Traceable to original collection point",
    acceptedAccurate: "Accurate within defined tolerance levels",
    acceptedCurrent: "Current (collected within specified timeframe)",
    acceptedContextualized: "Contextualized with relevant metadata",
    acceptedCited: "Properly cited and attributed",
    
    notAcceptedTitle: "Not Accepted",
    notAcceptedSynthetic: "Synthetic or AI-generated data without validation",
    notAcceptedUnverifiable: "Unverifiable claims or estimates",
    notAcceptedProprietary: "Proprietary data without permission",
    notAcceptedIncomplete: "Incomplete datasets missing key variables",
    notAcceptedBiased: "Biased samples not representative of population",
    
    // Validation Process
    validationTitle: "Validation Process",
    validationDescription: "A rigorous 5-step validation ensures data integrity and reliability.",
    
    validationStep1: "Initial Collection",
    validationStep1Desc: "Data gathered using standardized instruments and protocols",
    validationStep1Time: "1-2 days",
    validationStep2: "Quality Check",
    validationStep2Desc: "Automated and manual quality assurance procedures",
    validationStep2Time: "5-7 days",
    validationStep3: "Cross-Validation",
    validationStep3Desc: "Verification against multiple data sources",
    validationStep3Time: "5-7 days",
    validationStep4: "Expert Review",
    validationStep4Desc: "Subject matter expert evaluation and approval",
    validationStep4Time: "5-7 days",
    validationStep5: "Final Certification",
    validationStep5Desc: "Official certification and publication approval",
    validationStep5Time: "2-3 days",
    
    // Governance Structure
    governanceTitle: "Governance Structure Proposal",
    governanceDescription: "Clear governance ensures accountability, transparency, and effective decision-making.",
    
    governanceLevel1: "JCI Board of Directors",
    governanceLevel1Desc: "Strategic oversight and policy direction",
    governanceLevel2: "Development Councils",
    governanceLevel2Desc: "Data validation and regional coordination",
    governanceLevel3: "Executive Team",
    governanceLevel3Desc: "Operations and implementation",
    governanceLevel4: "Contributors & Users",
    governanceLevel4Desc: "Community feedback and engagement",
    
    // Update Cycles
    updateCyclesTitle: "Update Cycles",
    updateCycleAnnual: "Annual",
    updateCycleAnnualDesc: "Complete data refresh (Nov collection, Dec validation, Jan publication)",
    updateCycleIntermediate: "Intermediate",
    updateCycleIntermediateDesc: "Significant new research or program innovations",
    updateCycleContinuous: "Continuous",
    updateCycleContinuousDesc: "Error corrections, link updates, platform improvements",
    
    // Vision 2040 Alignment
    vision2040AlignmentTitle: "Alignment with JCI Vision 2040",
    strategicIntelligenceTitle: "Strategic Intelligence Function",
    strategicGoals: [
      { goal: "Leadership Development Excellence", contribution: "Identifies skills gaps (communication #1, critical thinking #2, leadership #3)" },
      { goal: "Technology & Digital Innovation", contribution: "Baseline digital adoption (jci.cc 13,351 users, 260K social followers)" },
      { goal: "Equity & Inclusion", contribution: "Maps regional opportunity inequality (22-point YFI gap)" },
      { goal: "Sustainability", contribution: "Global youth opportunity context for climate action alignment" }
    ],
    strategicRecommendationsTitle: "Strategic Recommendations",
    strategicRecommendations: [
      { area: "Where to Expand", recommendation: "Africa & Middle East (lowest YFI = highest impact potential)" },
      { area: "What to Prioritize", recommendation: "Mental health (40% YFI weight, biggest differentiator)" },
      { area: "How to Scale", recommendation: "Technology (80%+ adoption target by 2040)" },
      { area: "Success Measures", recommendation: "Improve global YFI 4-7 points; close Africa & ME gap 50%" }
    ],
    
    // Transparency Section
    transparencyTitle: "Transparency & Reproducibility",
    completeSourceInventoryTitle: "Complete Source Inventory",
    jciDocuments: "Official JCI Documents",
    jciDocumentsList: [
      "JCI Impact Report 2025",
      "JCI Vision 2040 Strategic Plan",
      "JCI 2025 Membership Experience Survey (3,790 respondents)",
      "JCI Area Development Council Plans of Action and Reports (2025)"
    ],
    globalResearchSources: "Global Research Sources",
    globalResearchSourcesList: [
      "WHO Mental Health Atlas 2024",
      "WHO Global Health Observatory 2024",
      "Global Burden of Disease Study 2023",
      "ILO Global Employment Trends 2024-25",
      "UNESCO Institute for Statistics",
      "World Bank (Human Capital Index, Ease of Doing Business)",
      "GEM Global Entrepreneurship Monitor 2024-25",
      "WEF Global Shapers Survey 2025",
      "Sapien Labs Mental State of the World 2024"
    ],
    formulaReproducibility: "Any researcher can reproduce this analysis",
    formulaExplanation: "YFI = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
    fullTransparency: "No proprietary methodologies, no black-box calculations, no hidden assumptions.",
    
    // Summary Section
    summaryTitle: "Research Methodology Status",
    summaryCompleted: "COMPLETED",
    summaryInProgress: "IN PROGRESS",
    summaryPlanned: "PLANNED",
    summaryCompletedItems: [
      "Combines verified data from JCI institutional sources and global indices",
      "Maps JCI's position within broader youth opportunity landscape",
      "Identifies priority gaps (especially mental health in Africa & ME)",
      "Establishes governance with 4 Development Councils",
      "Implements 5-step validation process",
      "Maintains zero synthetic data standard",
      "Provides strategic intelligence supporting Vision 2040"
    ],
    summaryInProgressItems: [
      "Expanding contributor base to 15+ National Organizations",
      "Scaling user platform to 200+",
      "Developing public APIs for researchers",
      "Planning regional deep dives"
    ],
    summaryPlannedItems: [
      "Building global partnerships (UN, WEF, UNESCO)",
      "Demonstrating policy influence in 10+ countries",
      "Creating global community of 5K+"
    ],
    
    // Expansion Roadmap
    roadmapTitle: "Expansion Roadmap",
    roadmapDescription: "A phased approach to global adoption and continuous improvement.",
    
    roadmap2025: "2025: Foundation",
    roadmap2025Desc: "Pilot implementation with 4 Development Councils, data standards established",
    roadmap2026: "2026: Expansion (BETA)",
    roadmap2026Desc: "15+ NOs contributing, 200+ users, public APIs for researchers",
    roadmap2027: "2027+: Global Authority",
    roadmap2027Desc: "UN/WEF/UNESCO integration, 1,000+ users, policy influence in 10+ countries",
    
    // Success Factors
    successTitle: "Success Factors",
    successDescription: "Key elements that contribute to successful methodology implementation.",
    
    successFactor1: "Strong executive sponsorship and commitment",
    successFactor2: "Clear communication and change management",
    successFactor3: "Adequate resources and dedicated teams",
    successFactor4: "Iterative approach with continuous feedback",
    successFactor5: "Technology enablement and automation",
    successFactor6: "Cultural sensitivity and regional adaptation",
    
    // Lessons Learned
    lessonsTitle: "Lessons Learned",
    lessonsDescription: "Insights from pilot implementations and stakeholder feedback.",
    
    lesson1: "Start with willing early adopters to build momentum",
    lesson2: "Invest heavily in training and capacity building",
    lesson3: "Balance standardization with local flexibility",
    lesson4: "Celebrate quick wins to maintain engagement",
    lesson5: "Build in feedback mechanisms from the start",
    
    // FAQ Section
    faqTitle: "Methodology FAQ",
    faqDescription: "Common questions about the research methodology.",
    
    faqQ1: "What data sources does the observatory use?",
    faqA1: "The observatory combines three primary sources: JCI institutional data (Impact Report, Membership Survey, Vision 2040), the Youth Futures Index covering 114 countries, and global research from WHO, ILO, UNESCO, World Bank, and other established sources.",
    
    faqQ2: "How is data quality ensured?",
    faqA2: "All data must pass a 7-criteria verification checklist: verifiable, auditable, traceable, accurate, current, contextualized, and properly cited. A 5-step validation process ensures integrity before publication.",
    
    faqQ3: "What does 'zero synthetic data' mean?",
    faqA3: "No projections, model-generated statistics, interpolated values, or estimated figures are presented as facts. All displayed metrics trace directly to verified sources. The only exception is Vision 2040 scenarios, which are clearly labeled as illustrative.",
    
    faqQ4: "How often is the data updated?",
    faqA4: "The observatory follows three update cycles: annual complete refresh (Nov-Jan), intermediate updates for significant new research, and continuous improvements for corrections and platform updates.",
    
    faqQ5: "Can researchers reproduce the analysis?",
    faqA5: "Yes. All source documents are publicly available, aggregation logic is documented, and the YFI formula (0.40×MH + 0.30×WF + 0.30×ENT) is transparent. Any researcher can reproduce and verify the results.",
    
    // CTA Section
    ctaTitle: "Ready to Explore the Observatory?",
    ctaDescription: "Access evidence-based insights to support your organization's strategic decisions.",
    ctaButton1: "Explore the Dashboard",
    ctaButton2: "How to Contribute",
    ctaButton3: "Read the FAQ",
    
    // Source Attribution
    sourceTitle: "Source",
    sourceDescription: "Methodology Version 3.1, January 2026",
    sourceNote: "Developed by JCI Development Councils in collaboration with global stakeholders"
  }
} as const;
